import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Avatar } from 'antd';
import type { InputRef } from 'antd';
import { SendOutlined, SmileOutlined } from '@ant-design/icons';
import styles from './LiveChat.module.scss';
import { useTranslation } from 'react-i18next';

interface Comment {
  id: string;
  username: string;
  avatar?: string;
  content: string;
  timestamp: string;
  isQuestion?: boolean;
}

interface LiveChatProps {
  className?: string;
}

const LiveChat: React.FC<LiveChatProps> = ({ className }) => {
      //国际化语言切换
      const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>([
    {
      id: '1',
      username: 'Danmaku Comments(999+)',
      content: 'How long will it take to deliver this?',
      timestamp: '16:59:25',
      avatar: '/src/assets/images/user1.jpg'
    },
    {
      id: '2', 
      username: 'User123',
      content: 'How long will it take to deliver this?',
      timestamp: '16:58:45',
      avatar: '/src/assets/images/user2.jpg'
    },
    {
      id: '3',
      username: 'ShopLover',
      content: 'How long',
      timestamp: '16:58:30',
      avatar: '/src/assets/images/user3.jpg'
    },
    {
      id: '4',
      username: 'BuyerReview',
      content: 'Yes',
      timestamp: '16:58:15',
      avatar: '/src/assets/images/user4.jpg'
    },
    {
      id: '5',
      username: 'CustomerOne',
      content: 'How long will it take to deliver this?',
      timestamp: '16:57:50',
      avatar: '/src/assets/images/user5.jpg'
    },
    {
      id: '6',
      username: 'QuestionAsker',
      content: 'How long',
      timestamp: '16:57:30',
      avatar: '/src/assets/images/user6.jpg'
    },
    {
      id: '7',
      username: 'LiveViewer',
      content: 'Yes',
      timestamp: '16:57:15',
      avatar: '/src/assets/images/user7.jpg'
    }
  ]);

  const [newComment, setNewComment] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);

  // 自动滚动到底部
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // 当有新评论时自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  // 发送评论
  const handleSendComment = () => {
    if (newComment.trim()) {
      const comment: Comment = {
        id: Date.now().toString(),
        username: 'You',
        content: newComment.trim(),
        timestamp: new Date().toLocaleTimeString('en-US', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit' 
        }),
        avatar: '/src/assets/images/current-user.jpg'
      };
      
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setIsTyping(false);
    }
  };

  // 处理Enter键发送
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendComment();
    }
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewComment(e.target.value);
    setIsTyping(e.target.value.length > 0);
  };

  // 生成随机颜色
  const generateAvatarColor = (username: string) => {
    const colors = [
      '#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068',
      '#f50', '#2db7f5', '#ff6b35', '#722ed1', '#52c41a'
    ];
    const index = username.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className={`${styles.liveChatContainer} ${className || ''}`}>
      {/* 聊天标题 */}
      <div className={styles.chatHeader}>
        <h3>{t('liveNow.DanmakuComments')}(999+)</h3>
      </div>

      {/* 聊天消息列表 */}
      <div className={styles.chatMessages} ref={chatContainerRef}>
        {comments.map((comment) => (
          <div key={comment.id} className={styles.messageItem}>
            <div className={styles.messageTime}>
              {comment.timestamp}
            </div>
            <div className={styles.messageContent}>
              <div className={styles.messageUser}>
                {comment.avatar ? (
                  <Avatar 
                    size={24} 
                    src={comment.avatar}
                    onError={() => true}
                  />
                ) : (
                  <Avatar 
                    size={24} 
                    style={{ 
                      backgroundColor: generateAvatarColor(comment.username),
                      fontSize: '12px'
                    }}
                  >
                    {comment.username.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <span className={styles.username}>
                  {comment.username}
                </span>
              </div>
              <div className={styles.messageText}>
                {comment.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 输入框区域 */}
      <div className={styles.chatInput}>
        <div className={styles.inputContainer}>
          <Input
            ref={inputRef}
            value={newComment}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={t('liveNow.BulletComment')}
            className={styles.messageInput}
            suffix={
              <div className={styles.inputActions}>
                <Button 
                  type="text" 
                  icon={<SmileOutlined />}
                  className={styles.emojiBtn}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendComment}
                  disabled={!newComment.trim()}
                  className={styles.sendBtn}
                >
                  {t('common.send')}
                </Button>
              </div>
            }
          />
        </div>
      </div>

      {/* 聊天统计信息 */}
      <div className={styles.chatStats}>
        <div className={styles.statsItem}>
          <span className={styles.statsNumber}>999+</span>
          <span className={styles.statsLabel}>{t('liveNow.Watchonline')}</span>
        </div>
        <div className={styles.statsItem}>
          <span className={styles.statsNumber}>{comments.length}</span>
          <span className={styles.statsLabel}>{t('liveNow.Barragemessages')}</span>
        </div>
      </div>
    </div>
  );
};

export default LiveChat;
